/**
 * Arabic — right to left.
 *
 * This is the locale that proves the i18n work is real rather than string
 * swapping. Two things it forces:
 *
 *   1. DIRECTION. The whole document flips, so any layout built on `left`/
 *      `right` instead of `start`/`end` breaks visibly here.
 *   2. PLURALS. Arabic has all six CLDR categories, and the rule for `few`
 *      versus `many` depends on the number modulo 100 — 3 items is "few",
 *      13 is "many", 103 is "few" again. English's one/other cannot express
 *      that, which is why the catalogue type allows every form and Intl picks.
 */
export const ar = {
    common: {
        save: 'حفظ',
        saving: 'جارٍ الحفظ…',
        saved: 'تم الحفظ',
        cancel: 'إلغاء',
        delete: 'حذف',
        edit: 'تعديل',
        close: 'إغلاق',
        back: 'رجوع',
        retry: 'إعادة المحاولة',
        loading: 'جارٍ التحميل…',
        search: 'بحث',
        signOut: 'تسجيل الخروج',
        store: 'المتجر',
        status: 'الحالة',
        email: 'البريد الإلكتروني',
        password: 'كلمة المرور',
        firstName: 'الاسم الأول',
        lastName: 'اسم العائلة',
        phone: 'الهاتف',
        total: 'الإجمالي',
        subtotal: 'المجموع الفرعي',
        quantity: 'الكمية',
        price: 'السعر',
        stock: 'المخزون',
        language: 'اللغة',
        never: 'أبدًا',
        none: '—',
    },
    nav: {
        products: 'المنتجات',
        orders: 'الطلبات',
        customers: 'العملاء',
        settings: 'الإعدادات',
        website: 'الموقع',
        apps: 'التطبيقات',
        themes: 'القوالب',
        installs: 'عمليات التثبيت',
    },
    auth: {
        signInTitle: 'تسجيل الدخول',
        signInSubtitle: 'سجّل الدخول إلى لوحة إدارة متجرك.',
        partnerSubtitle: 'طوّر تطبيقات وقوالب للتجار.',
        signIn: 'تسجيل الدخول',
        signingIn: 'جارٍ تسجيل الدخول…',
        createStore: 'ابدأ متجرك',
        creatingStore: 'جارٍ إنشاء متجرك…',
        haveAccount: 'لديك متجر بالفعل؟ سجّل الدخول',
        needAccount: 'ليس لديك متجر بعد؟ أنشئ واحدًا',
        storeName: 'اسم المتجر',
        storeAddress: 'عنوان المتجر',
        passwordHint: '١٢ حرفًا على الأقل',
        slugChecking: 'جارٍ التحقق من التوفّر…',
        slugTaken: '{{slug}} محجوز. نقترح {{suggestion}}.',
        slugFree: '{{slug}} متاح.',
    },
    products: {
        title: 'المنتجات',
        searchPlaceholder: 'ابحث بالعنوان أو المعرّف أو المورّد…',
        newProduct: 'منتج جديد',
        columnProduct: 'المنتج',
        columnPrice: 'السعر',
        columnStock: 'المخزون',
        columnVariants: 'المتغيّرات',
        statusActive: 'نشط',
        statusDraft: 'مسودة',
        statusArchived: 'مؤرشف',
        empty: 'لا توجد منتجات بعد. أضف أول منتج لتبدأ البيع.',
        variantCount: {
            zero: 'لا توجد متغيّرات',
            one: 'متغيّر واحد',
            two: 'متغيّران',
            few: '{{count}} متغيّرات',
            many: '{{count}} متغيّرًا',
            other: '{{count}} متغيّر',
        },
        stockLevel: {
            zero: 'نفدت الكمية',
            one: 'قطعة واحدة في المخزون',
            two: 'قطعتان في المخزون',
            few: '{{count}} قطع في المخزون',
            many: '{{count}} قطعة في المخزون',
            other: '{{count}} قطعة في المخزون',
        },
        variantTitle: 'اسم المتغيّر',
        sku: 'رمز المنتج',
        couldNotLoadVariants: 'تعذّر تحميل المتغيّرات.',
    },
    orders: {
        title: 'الطلبات',
        searchPlaceholder: 'ابحث برقم الطلب أو البريد الإلكتروني…',
        columnOrder: 'الطلب',
        columnCustomer: 'العميل',
        columnPayment: 'الدفع',
        columnFulfilment: 'التجهيز',
        columnPlaced: 'تاريخ الطلب',
        shipTo: 'الشحن إلى',
        item: 'الصنف',
        qty: 'الكمية',
        unit: 'سعر الوحدة',
        empty: 'لا توجد طلبات بعد.',
        itemCount: {
            zero: 'لا أصناف',
            one: 'صنف واحد',
            two: 'صنفان',
            few: '{{count}} أصناف',
            many: '{{count}} صنفًا',
            other: '{{count}} صنف',
        },
        financial: {
            PENDING: 'بانتظار الدفع',
            AUTHORIZED: 'مُصرّح به',
            PAID: 'مدفوع',
            PARTIALLY_REFUNDED: 'مسترد جزئيًا',
            REFUNDED: 'مسترد',
            VOIDED: 'ملغى',
        },
        fulfilment: {
            UNFULFILLED: 'غير مُجهّز',
            PARTIAL: 'مُجهّز جزئيًا',
            FULFILLED: 'مُجهّز',
            RESTOCKED: 'أُعيد للمخزون',
        },
    },
    customers: {
        title: 'العملاء',
        searchPlaceholder: 'ابحث بالاسم أو البريد الإلكتروني…',
        newCustomer: 'عميل جديد',
        columnCustomer: 'العميل',
        columnOrders: 'الطلبات',
        columnMarketing: 'التسويق',
        lifetimeSpend: 'إجمالي الإنفاق',
        acceptsMarketing: 'يقبل التسويق',
        empty: 'لا يوجد عملاء بعد.',
        orderCount: {
            zero: 'لا طلبات',
            one: 'طلب واحد',
            two: 'طلبان',
            few: '{{count}} طلبات',
            many: '{{count}} طلبًا',
            other: '{{count}} طلب',
        },
    },
    settings: {
        title: 'إعدادات المتجر',
        storefront: 'واجهة متجرك',
        storefrontHint: 'المتجر يعمل الآن. في بيئة التطوير المحلية لا يكون النطاق الفرعي مسجّلًا في DNS — بل يثبّت خادم التطوير ترويسة Host بدلًا من ذلك.',
        details: 'التفاصيل',
        customDomain: 'نطاق مخصّص',
        customDomainHint: 'وجّه سجل CNAME إلى Upweo، ثم أدخل اسم المضيف فقط. اتركه فارغًا لاستخدام العنوان أعلاه.',
        addressHint: '{{host}} — تغييره سيُعطّل الروابط الحالية',
        currency: 'العملة',
        country: 'البلد',
        plan: 'الخطة',
        currencyLocked: 'لا يمكن تغيير العملة بعد وجود طلبات — ستفقد الإجماليات السابقة معناها.',
        languages: 'اللغات',
        defaultLanguage: 'اللغة الافتراضية',
        defaultLanguageHint: 'ما يراه المتسوّق عندما يطلب متصفّحه لغة لا توفّرها.',
        storefrontLanguages: 'لغات المتجر',
        storefrontLanguagesHint: 'يمكن للمتسوّقين التبديل بين اللغات التي تحدّدها. ألغِ تحديد لغة لإخفائها بدلًا من عرض متجر مترجَم جزئيًا.',
        saveFailed: 'تعذّر حفظ الإعدادات',
    },
    payments: {
        title: 'المدفوعات',
        testMode: 'وضع الاختبار',
        live: 'مباشر',
        needsWebhook: 'يحتاج إلى Webhook',
        notConnected: 'غير متصل',
        account: 'الحساب',
        secretKey: 'المفتاح السري',
        publishableKey: 'المفتاح العلني',
        webhookSecret: 'مفتاح توقيع الـ Webhook',
        verified: 'تم التحقق',
        webhookEndpoint: 'عنوان الـ Webhook',
        connect: 'ربط Stripe',
        update: 'تحديث المفاتيح',
        disconnect: 'قطع الاتصال',
        verifying: 'جارٍ التحقق…',
        savedAndVerified: 'تم الحفظ والتحقق',
        copy: 'نسخ',
        copied: 'تم النسخ',
        disabledNotConnected: 'الدفع معطّل حتى تربط حساب Stripe. يمكن للمتسوّقين التصفّح وملء السلة، لكن لا يمكنهم الدفع.',
        disabledNoWebhook: 'تم حفظ مفاتيحك، لكن الدفع يبقى معطّلًا حتى تضيف مفتاح توقيع الـ Webhook. بدونه يمكننا بدء الدفع دون أن نعرف أبدًا أنه نجح — فتبقى الطلبات غير مدفوعة بينما يصل المال إلى Stripe.',
        secretKeyHintNew: 'يبدأ بـ sk_ أو rk_. يُخزَّن مشفّرًا ولا يُعرض مرة أخرى.',
        secretKeyHintExisting: 'اتركه فارغًا للإبقاء على المفتاح المحفوظ.',
        publishableKeyHint: 'يبدأ بـ pk_. يُرسَل إلى متصفّح المتسوّق، فهو علني بطبيعته.',
        webhookSecretHint: 'يبدأ بـ whsec_. يعرضه Stripe مرة واحدة عند إنشاء العنوان أعلاه.',
        saveFailed: 'تعذّر حفظ إعدادات الدفع',
        disconnectFailed: 'تعذّر قطع الاتصال',
    },
    shop: {
        shopAll: 'تسوّق الكل',
        searchPlaceholder: 'بحث…',
        soldOut: 'نفد',
        addToCart: 'أضف إلى السلة',
        adding: 'جارٍ الإضافة…',
        options: 'الخيارات',
        productNotFound: 'المنتج غير موجود',
        continueShopping: 'متابعة التسوّق',
        poweredBy: '{{store}} — مدعوم بـ Upweo',
        cart: 'السلة',
        cartEmpty: 'سلّتك فارغة',
        decreaseQuantity: 'إنقاص الكمية',
        increaseQuantity: 'زيادة الكمية',
        remove: 'إزالة',
        taxNotice: 'لم تُحتسب الضرائب والشحن بعد.',
        checkout: 'إتمام الشراء',
        contact: 'بيانات التواصل',
        shippingAddress: 'عنوان الشحن',
        address: 'العنوان',
        city: 'المدينة',
        postcode: 'الرمز البريدي',
        country: 'البلد',
        orderSummary: 'ملخّص الطلب',
        editCart: 'تعديل السلة',
        continueToPayment: 'المتابعة إلى الدفع · {{total}}',
        preparingPayment: 'جارٍ تحضير الدفع…',
        payment: 'الدفع',
        pay: 'ادفع {{total}}',
        paying: 'جارٍ الدفع…',
        paymentSecurity: 'تُعالَج المدفوعات عبر Stripe. لا تصل بيانات بطاقتك إلى هذا المتجر أبدًا.',
        paymentFailed: 'فشل الدفع. جرّب بطاقة أخرى.',
        thankYou: 'شكرًا لك',
        orderPaid: 'تم دفع الطلب {{order}} بقيمة {{total}}.',
        confirmationSent: 'سيصلك تأكيد على {{email}}.',
        cartCount: {
            zero: 'السلة',
            one: 'السلة (صنف واحد)',
            two: 'السلة (صنفان)',
            few: 'السلة ({{count}})',
            many: 'السلة ({{count}})',
            other: 'السلة ({{count}})',
        },
    },
    partner: {
        title: 'Upweo Partners',
        apps: 'التطبيقات',
        newApp: 'تطبيق جديد',
        clientId: 'معرّف العميل',
        clientSecret: 'سر العميل',
        scopes: 'الصلاحيات',
        requestedScopes: 'الصلاحيات المطلوبة',
        installs: 'عمليات التثبيت',
        grantedScopes: 'الصلاحيات الممنوحة',
        installed: 'مثبَّت',
        themes: 'القوالب',
        noThemes: 'لا توجد قوالب بعد.',
        noApps: 'لا توجد تطبيقات بعد.',
        activePartnerAccount: 'حساب الشريك النشط',
        installCount: {
            zero: 'غير مثبَّت',
            one: 'تثبيت واحد',
            two: 'تثبيتان',
            few: '{{count}} عمليات تثبيت',
            many: '{{count}} عملية تثبيت',
            other: '{{count}} عملية تثبيت',
        },
    },
    website: {
        subtitle: 'الموقع العام على upweo.com',
        copy: 'نصوص التسويق',
        copyHint: 'اترك الحقل فارغًا لاستخدام النص الذي يوفّره Upweo. النص الباهت هو الافتراضي الحالي لهذه اللغة.',
        clearHint: 'إفراغ الحقل يعيد النص الافتراضي.',
        pricing: 'خطط الأسعار',
        pricingHint: 'تظهر الأسعار بتنسيق الأرقام الخاص بكل زائر. النص وحده يُترجم — أما الرقم فهو نفسه في كل مكان.',
        published: 'منشور',
        hidden: 'مخفي',
        publish: 'نشر',
        hide: 'إخفاء',
        popular: 'الأكثر اختيارًا',
    },
    site: {
        nav: {
            features: 'المزايا',
            pricing: 'الأسعار',
            partners: 'الشركاء',
            signIn: 'تسجيل الدخول',
            startFree: 'ابدأ مجانًا',
        },
        hero: {
            title: 'كل ما تحتاجه للبيع عبر الإنترنت',
            subtitle: 'يمنحك Upweo متجرًا وصفحة دفع وأدوات إدارة العمل من خلفهما — بلغتك وبعملتك وعلى نطاقك الخاص.',
            ctaPrimary: 'أنشئ متجرك',
            ctaSecondary: 'اطّلع على الأسعار',
            note: 'لا حاجة لبطاقة للبدء.',
        },
        features: {
            heading: 'مبني لمتاجر حقيقية',
            subheading: 'الأجزاء المُرهِقة في أي تجارة إلكترونية، جاهزة بالفعل.',
            f1Title: 'متجرك الخاص',
            f1Body: 'يحصل كل متجر على عنوانه فور التسجيل، ويمكنه الانتقال إلى نطاقك الخاص متى شئت.',
            f2Title: 'المنتجات والمخزون',
            f2Body: 'متغيّرات لكل منها سعرها ورمزها، ومخزون يُتابَع لكل موقع، ويُحجز فور تقديم الطلب.',
            f3Title: 'مدفوعات حقيقية',
            f3Body: 'اربط حساب Stripe الخاص بك. أموال عملائك تصل إليك مباشرة — ولا نقف في المنتصف أبدًا.',
            f4Title: 'يتحدث لغة عملائك',
            f4Body: 'ست لغات، ودعم الكتابة من اليمين إلى اليسار، وأسعار بصيغة كل بلد. وتصل الإيصالات باللغة التي كان المتسوّق يقرأ بها.',
            f5Title: 'الطلبات والعملاء',
            f5Body: 'كل طلب مُوثَّق بلحظة الشراء، فيبقى سجلك دقيقًا مهما تغيّر الكتالوج لاحقًا.',
            f6Title: 'التطبيقات والقوالب',
            f6Body: 'منظومة شركاء مع OAuth وصلاحيات محدودة و Webhooks، لتوسيع متجرك دون المساس ببياناتك.',
        },
        pricing: {
            heading: 'أسعار بسيطة',
            subheading: 'ابدأ مجانًا، وارتقِ حين ترتقي الطلبات.',
            perMonth: '/شهريًا',
            cta: 'ابدأ الآن',
            popular: 'الأكثر اختيارًا',
            placeholderNotice: 'هذه الأرقام مؤقتة ما دام Upweo في النسخة التجريبية. لا يُحتسب أي مبلغ اليوم.',
            empty: 'يجري الآن تحديد الأسعار. وإنشاء متجر مجاني في هذه الأثناء.',
        },
        partners: {
            heading: 'طوّر مع Upweo',
            subheading: 'اصنع تطبيقات وقوالب للتجار، بتدفّق OAuth حقيقي وصلاحيات محدودة و Webhooks.',
            cta: 'كن شريكًا',
            signIn: 'دخول الشركاء',
            b1Title: 'واجهات برمجية حقيقية',
            b1Body: 'واجهة GraphQL عامة للمتجر وواجهة REST للإدارة، وكلتاهما بإصدارات.',
            b2Title: 'وصول محدود',
            b2Body: 'يمنح التجار الصلاحيات التي يطلبها تطبيقك بالضبط، ويمكنهم سحبها.',
            b3Title: 'Webhooks',
            b3Body: 'اشترك في الأحداث التي تهمّك، بتسليم موقَّع وإعادة محاولة تلقائية.',
        },
        signup: {
            heading: 'أنشئ متجرك',
            subheading: 'يستغرق الأمر دقيقة تقريبًا، ويمكن تغيير كل ذلك لاحقًا.',
            submit: 'أنشئ متجري',
            submitting: 'جارٍ إنشاء متجرك…',
            haveAccount: 'تبيع بالفعل عبر Upweo؟ سجّل الدخول',
            success: 'متجرك جاهز. ننقلك الآن إلى لوحة التحكم…',
        },
        partnerSignup: {
            heading: 'كن شريكًا',
            subheading: 'عرّفنا بنفسك. نراجع الشركاء الجدد قبل نشر تطبيقاتهم.',
            accountName: 'اسم الشركة أو المطوّر',
            submit: 'إنشاء حساب شريك',
            submitting: 'جارٍ إنشاء حسابك…',
            success: 'أُنشئ حساب الشريك وهو بانتظار المراجعة. ننقلك إلى البوابة…',
            haveAccount: 'شريك بالفعل؟ سجّل الدخول',
            existingUserHint: 'لديك حساب Upweo؟ استخدم البريد وكلمة المرور نفسيهما وسنضيف صلاحية الشريك إليه.',
        },
        footer: {
            tagline: 'تجارة متعددة المتاجر، مبنية بشكل مفتوح.',
            product: 'المنتج',
            forPartners: 'للشركاء',
            rights: '© {{year}} Upweo',
        },
    },
    errors: {
        BAD_REQUEST: 'تعذّر فهم هذا الطلب.',
        VALIDATION_FAILED: 'يرجى مراجعة الحقول المحدّدة.',
        UNAUTHENTICATED: 'يرجى تسجيل الدخول مرة أخرى.',
        FORBIDDEN: 'ليست لديك صلاحية للقيام بذلك.',
        TENANT_NOT_RESOLVED: 'لم نتمكّن من تحديد المتجر المقصود.',
        NOT_FOUND: 'لم يتم العثور على ذلك.',
        CONFLICT: 'هذا يتعارض مع شيء موجود بالفعل.',
        RATE_LIMITED: 'محاولات كثيرة. يرجى الانتظار قليلًا.',
        PAYMENT_FAILED: 'تعذّر إتمام الدفع.',
        INTERNAL_ERROR: 'حدث خطأ من جانبنا.',
        network: 'تعذّر الوصول إلى الخادم. تحقّق من اتصالك.',
    },
    email: {
        orderConfirmation: {
            subject: 'تأكيد الطلب {{order}}',
            preheader: 'استلمنا طلبك ودفعتك.',
            heading: 'شكرًا لطلبك',
            greeting: 'مرحبًا {{name}}،',
            intro: 'استلم {{store}} طلبك وتمت الموافقة على دفعتك.',
            orderLabel: 'الطلب',
            datePlaced: 'تاريخ الطلب',
            itemsHeading: 'ما طلبته',
            subtotal: 'المجموع الفرعي',
            shipping: 'الشحن',
            tax: 'الضريبة',
            total: 'الإجمالي',
            shippingTo: 'الشحن إلى',
            nextSteps: 'سنراسلك مرة أخرى عند شحن طلبك.',
            footer: 'أُرسلت هذه الرسالة من {{store}}.',
        },
        passwordReset: {
            subject: 'إعادة تعيين كلمة مرور Upweo',
            preheader: 'رابط لاختيار كلمة مرور جديدة.',
            heading: 'إعادة تعيين كلمة المرور',
            intro: 'طلب أحدهم إعادة تعيين كلمة المرور لـ {{email}}.',
            action: 'اختيار كلمة مرور جديدة',
            expiry: 'يعمل هذا الرابط مرة واحدة وتنتهي صلاحيته خلال {{minutes}} دقيقة.',
            ignore: 'إذا لم تكن أنت، فتجاهل هذه الرسالة — لم تتغيّر كلمة مرورك.',
            footer: 'أُرسلت من Upweo.',
        },
    },
};
//# sourceMappingURL=ar.js.map